import type { ReactNode } from 'react'

import { cn } from '../../lib/utils'

export type ReviewStateTone = 'accept' | 'deny'

interface Props {
  isActive: boolean
  /** True when the other tone is selected (visually defers). */
  isMuted?: boolean
  tone: ReviewStateTone
  onClick: () => void
  disabled?: boolean
  dataTourId?: string
  children: ReactNode
}

/**
 * Accept / Deny / Complete / Reject pill used on finding cards. Active gets
 * a saturated border + ring; the counterpart fades when the other tone wins.
 */
export function ReviewStateButton({
  isActive,
  isMuted,
  tone,
  onClick,
  disabled,
  dataTourId,
  children
}: Props) {
  const idle =
    tone === 'accept'
      ? 'bg-st-run-bg text-st-run border-st-run/30 hover:border-st-run/70'
      : 'bg-st-down-bg text-st-down border-st-down/30 hover:border-st-down/70'
  const active =
    tone === 'accept'
      ? 'bg-st-run-bg text-st-run border-st-run ring-2 ring-st-run/40 font-semibold'
      : 'bg-st-down-bg text-st-down border-st-down ring-2 ring-st-down/40 font-semibold'
  const glyph = tone === 'accept' ? '✓' : '✕'
  return (
    <button
      data-tour-id={dataTourId}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={isActive}
      className={cn(
        'inline-flex cursor-pointer items-center gap-1 rounded-md border px-2.5 py-0.5 text-11',
        'transition-[background-color,border-color,box-shadow,color,opacity] duration-150 ease-out',
        isActive ? active : idle,
        isMuted && 'opacity-40',
        disabled && 'cursor-not-allowed opacity-50'
      )}
    >
      <span aria-hidden className="text-10 leading-none">
        {glyph}
      </span>
      <span>{children}</span>
    </button>
  )
}
